import type {
  AddendumDraft,
  SectionAddendum,
  SectionAddendumAttachment,
} from 'pages/reporting/reports/rug/rugReportData'
import { z } from 'zod'

export const addendumDraftSchema = z.object({
  path: z.string(),
  fingerprint: z.string(),
})

export interface AddendumPageContent {
  attachment?: SectionAddendumAttachment
  sourcePageIndex?: number
}

export function getAddendumPages(
  addendum: SectionAddendum
): AddendumPageContent[] {
  if (addendum.mode !== 'attachments') {
    if (
      !Number.isSafeInteger(addendum.addedPageCount) ||
      addendum.addedPageCount < 1
    ) {
      throw new Error('Specify a positive whole number of blank pages')
    }
    return Array.from({ length: addendum.addedPageCount }, () => ({}))
  }

  return (addendum.attachments ?? []).flatMap((attachment) => {
    const count = attachment.kind === 'pdf' ? attachment.pageCount : 1
    if (!Number.isSafeInteger(count) || count < 1 || count > 100) {
      throw new Error(`Invalid page count for ${attachment.fileName}`)
    }
    return Array.from({ length: count }, (_, sourcePageIndex) => ({
      attachment,
      sourcePageIndex,
    }))
  })
}

export function getAddendumFingerprint(addendums: SectionAddendum[]): string {
  return JSON.stringify(
    addendums.map((addendum) => ({
      sectionId: addendum.sectionId,
      pageSubtitle: addendum.pageSubtitle ?? '',
      content:
        addendum.mode === 'attachments'
          ? (addendum.attachments ?? []).map((file) => ({
              kind: file.kind,
              path: file.path,
              caption: file.caption ?? '',
              pageCount: file.kind === 'pdf' ? file.pageCount : 1,
            }))
          : addendum.addedPageCount,
    }))
  )
}

export function getAddendumDraftBlocker(
  addendums: SectionAddendum[],
  draft: AddendumDraft | undefined
): string | undefined {
  if (!draft && addendums.length === 0) return undefined
  return draft?.fingerprint === getAddendumFingerprint(addendums)
    ? undefined
    : 'Regenerate the draft PDF to include the latest addendum changes before signing or publishing'
}
